import React, { useEffect, useMemo, useRef } from 'react';
import { FiInstagram, FiExternalLink } from 'react-icons/fi';
import { useColorScheme } from '../../contexts/ColorSchemeContext';
import baseline from '../../app-ui/baseline';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import './FeedDetail.css';
import DOMPurify from 'dompurify';
import AutomaticFeedMedia from './AutomaticFeedMedia';
import { createMediaViewport } from './mediaViewport';

// Keep the app's accepted providers and identities; never embed an arbitrary URL.
export function parseFeedMedia(raw) {
  try {
    const url = new URL(raw.trim());
    if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password || url.port)
      return null;
    const host = url.hostname.toLowerCase().replace(/^www\./, '');
    const parts = url.pathname.split('/').filter(Boolean);
    if (['youtube.com', 'm.youtube.com', 'youtu.be', 'youtube-nocookie.com'].includes(host)) {
      const id =
        host === 'youtu.be'
          ? parts[0]
          : parts[0] === 'watch'
            ? url.searchParams.get('v')
            : ['embed', 'shorts', 'live'].includes(parts[0])
              ? parts[1]
              : null;
      if (!/^[\w-]{11}$/.test(id || '')) return null;
      const time = url.searchParams.get('start') || url.searchParams.get('t') || '';
      const match = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?$/.exec(time);
      const start = match
        ? Math.min(
            86400,
            Number(match[1] || 0) * 3600 + Number(match[2] || 0) * 60 + Number(match[3] || 0),
          )
        : 0;
      return {
        provider: 'YouTube',
        url: `https://www.youtube.com/watch?v=${id}${start ? `&t=${start}s` : ''}`,
        embed: `https://www.youtube-nocookie.com/embed/${id}?playsinline=1&autoplay=0&start=${start}`,
      };
    }
    if (
      host === 'instagram.com' &&
      ['p', 'reel', 'tv'].includes(parts[0]) &&
      /^[\w-]{1,64}$/.test(parts[1] || '') &&
      parts.length === 2
    ) {
      const canonical = `https://www.instagram.com/${parts[0]}/${parts[1]}/`;
      return { provider: 'Instagram', url: canonical, embed: `${canonical}embed/` };
    }
    if (['x.com', 'twitter.com', 'mobile.twitter.com', 'mobile.x.com'].includes(host)) {
      const id =
        parts[0] === 'i' && parts[1] === 'web' && parts[2] === 'status'
          ? parts[3]
          : parts[1] === 'status'
            ? parts[2]
            : null;
      if (/^\d{1,25}$/.test(id || '') && /^\w+$/.test(parts[0])) {
        const path = parts[0] === 'i' ? `i/web/status/${id}` : `${parts[0]}/status/${id}`;
        return { provider: 'X', url: `https://x.com/${path}`, id };
      }
    }
  } catch {
    /* Unsupported links stay ordinary links. */
  }
  return null;
}

const safeWebUrl = (raw) => {
  if (!raw.trim()) return false;
  try {
    const url = new URL(raw, window.location.origin);
    return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password;
  } catch {
    return false;
  }
};

export function feedBlocks(html) {
  const fragment = DOMPurify.sanitize(html || '', {
    RETURN_DOM_FRAGMENT: true,
    ALLOWED_TAGS: [
      'p',
      'br',
      'span',
      'strong',
      'b',
      'em',
      'i',
      'u',
      's',
      'del',
      'strike',
      'a',
      'img',
      'h1',
      'h2',
      'h3',
      'h4',
      'blockquote',
      'ul',
      'ol',
      'li',
      'pre',
      'code',
      'table',
      'tbody',
      'tr',
      'td',
      'th',
    ],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'style', 'class', 'colspan', 'rowspan', 'width', 'align'],
  });
  fragment.querySelectorAll('*').forEach((node) => {
    const classes = Array.from(node.classList).filter((value) =>
      /^ql-(size-(small|large|huge)|align-(center|right|justify))$/.test(value),
    );
    node.removeAttribute('class');
    if (classes.length) node.setAttribute('class', classes.join(' '));
    // Preserve editorial typography, not layout/overlays or CSS network requests.
    const style = node.style;
    const retained = [
      'color',
      'background-color',
      'font-size',
      'font-weight',
      'font-style',
      'text-align',
      'text-decoration',
    ]
      .map((key) => [key, style.getPropertyValue(key)])
      .filter(([, value]) => value && !/url\s*\(|expression|var\s*\(/i.test(value));
    let imageAlign, imageWidth;
    if (node.tagName === 'IMG') {
      imageWidth = node.style.width || node.getAttribute('width');
      imageAlign =
        node.style.cssFloat ||
        (/margin\s*:\s*(?:0(?:px)?\s+)?auto/i.test(node.getAttribute('style') || '')
          ? 'center'
          : node.getAttribute('align'));
      let parent = node.parentElement;
      while (!imageAlign && parent) {
        imageAlign =
          /\bql-align-(center|right|left)\b/.exec(parent.className)?.[1] || parent.style.textAlign;
        parent = parent.parentElement;
      }
    }
    node.removeAttribute('style');
    retained.forEach(([key, value]) => {
      const px = key === 'font-size' && /^(\d+(?:\.\d+)?)px$/.exec(value);
      node.style.setProperty(key, px ? `${Number(px[1]) / 16}rem` : value);
    });
    if (node.style.fontSize) node.style.lineHeight = '1.6';
    if (node.tagName === 'A') {
      if (!safeWebUrl(node.getAttribute('href') || '')) node.removeAttribute('href');
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
    if (node.tagName === 'IMG') {
      if (!safeWebUrl(node.getAttribute('src') || '')) node.remove();
      else {
        if (/^\d+(?:\.\d+)?(?:px|%)?$/.test(imageWidth || ''))
          node.style.width = /^\d+(?:\.\d+)?$/.test(imageWidth) ? `${imageWidth}px` : imageWidth;
        if (imageAlign === 'center' || imageAlign === 'right') node.style.marginLeft = 'auto';
        if (imageAlign === 'center') node.style.marginRight = 'auto';
        node.setAttribute('loading', 'lazy');
        node.setAttribute('referrerpolicy', 'no-referrer');
      }
    }
  });
  // Build a sanitized tree so media inside lists/quotes keeps its parent structure.
  const convert = (node) => {
    if (node.nodeType === 3) return { text: node.textContent, html: node.textContent };
    if (node.nodeType !== 1) return { text: '', html: '' };
    if (node.nodeName === 'P' && /^(?:\s|&nbsp;|<br\s*\/?\s*>)*$/i.test(node.innerHTML))
      return {
        html: '<div class="postpop-editor-spacer"></div>',
        tag: 'div',
        props: { className: 'postpop-editor-spacer' },
        children: [],
      };
    if (node.nodeName === 'P' && !node.querySelector('img')) {
      const children = Array.from(node.childNodes).filter(
        (child) => child.nodeType !== 3 || child.textContent.trim(),
      );
      const only = children.length === 1 ? children[0] : null;
      const raw = only?.nodeName === 'A' ? only.getAttribute('href') : node.textContent.trim();
      const media = parseFeedMedia(raw || '');
      if (media) return { media };
    }
    const names = {
      class: 'className',
      colspan: 'colSpan',
      rowspan: 'rowSpan',
      referrerpolicy: 'referrerPolicy',
    };
    const props = {};
    for (const { name, value } of Array.from(node.attributes)) {
      if (name !== 'style') props[names[name] || name] = value;
    }
    if (node.style.length) {
      props.style = {};
      for (const key of Array.from(node.style)) {
        props.style[key.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())] =
          node.style.getPropertyValue(key);
      }
    }
    return {
      html: node.outerHTML,
      tag: node.tagName.toLowerCase(),
      props,
      children: Array.from(node.childNodes).map(convert),
    };
  };
  return Array.from(fragment.childNodes).map(convert);
}

export default function FeedBody({ html }) {
  const { blocks, viewport } = useMemo(
    () => ({ blocks: feedBlocks(html), viewport: createMediaViewport() }),
    [html],
  );
  const { resolvedMode } = useColorScheme();
  const colors = baseline.tokens[resolvedMode === 'dark' ? 'darkColors' : 'lightColors'];
  const root = useRef(null);
  useEffect(() => viewport.start(root.current), [viewport]);
  const { locale } = useWebLocale(),
    en = locale === 'en';
  const renderBlock = (block, index) => {
    if ('text' in block) return block.text;
    if (!block.media)
      return React.createElement(
        block.tag,
        { ...block.props, key: index },
        ...block.children.map((child, childIndex) => renderBlock(child, `${index}.${childIndex}`)),
      );
    return block.media.provider === 'YouTube' ? (
      <AutomaticFeedMedia
        key={`${index}-${block.media.url}`}
        id={String(index)}
        media={block.media}
        viewport={viewport}
      />
    ) : (
      <a
        className="pp-feed-external"
        key={`${index}-${block.media.url}`}
        href={block.media.url}
        target="_blank"
        rel="noopener noreferrer"
      >
        {block.media.provider === 'Instagram' ? (
          <FiInstagram size={24} aria-hidden="true" />
        ) : (
          <span aria-hidden="true" style={{ flex: '0 0 24px', textAlign: 'center' }}>
            X
          </span>
        )}
        <span>
          <strong>
            {en ? `View on ${block.media.provider}` : `${block.media.provider}에서 보기`}
          </strong>
          <small>
            {en ? 'Opens in an external app or browser.' : '외부 앱 또는 브라우저에서 열려요.'}
          </small>
        </span>
        <FiExternalLink size={18} aria-hidden="true" />
      </a>
    );
  };
  return (
    <div
      ref={root}
      className="pp-feed-rich"
      style={{
        '--settings-text': colors.text,
        '--settings-muted': colors.textSecondary,
        '--settings-border': colors.divider,
        '--settings-surface': colors.surfaceSoft,
        '--settings-background': colors.background,
      }}
    >
      {blocks.map((block, index) => renderBlock(block, String(index)))}
    </div>
  );
}
